import { useQuery } from '@tanstack/react-query';
import { bookings, identity, isApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { getResolvedLocale } from '@repletoryreach/sdk-utils/date';
import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';
import type { Translate } from '../../../i18n/I18nProvider';
import { DEFAULT_TZ } from './time';

/** Every bookings query starts with this key, so one invalidation refreshes the lot. */
export const BOOKINGS_KEY = 'bookings';

export function useBookingSettings() {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'settings'],
    queryFn: () => bookings.getSettings(),
    staleTime: 60_000
  });
}

export function useServices(includeInactive = false) {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'services', includeInactive],
    queryFn: () => bookings.listServices({ include_inactive: includeInactive }),
    staleTime: 30_000
  });
}

export function useStaff() {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'staff'],
    queryFn: () => bookings.listStaff(),
    staleTime: 30_000
  });
}

/**
 * The organisation's time zone: the signed-in session carries it; the
 * business hours answer it too, for an older session that does not.
 */
export function useOrgTimezone(): string {
  const { user } = useAuth();
  const fromUser = user?.org?.timezone;
  const hours = useQuery({
    queryKey: ['business-hours'],
    queryFn: () => identity.getBusinessHours(),
    enabled: !fromUser,
    staleTime: 5 * 60_000
  });
  return fromUser || hours.data?.data.timezone || DEFAULT_TZ;
}

/** The language dates are written in, as the console's language picker set it. */
export function useDateLocale(): string {
  return getResolvedLocale();
}

/** What went wrong, in the customer's words. */
export function bookingErrorText(t: Translate, e: unknown): string {
  const code = bookings.bookingErrorCode(e);
  if (code) return t(`bookings.error.${code}`);
  if (isApiError(e) && e.status === 403) return t('bookings.error.forbidden');
  if (isApiError(e) && e.status === 503) return t('bookings.error.unavailable');
  return e instanceof Error && e.message ? e.message : t('common.unknownError');
}

export function statusTone(status: string): StatusTone {
  switch (status) {
    case 'confirmed': return 'healthy';
    case 'requested': return 'pending';
    case 'completed': return 'inactive';
    case 'declined': return 'down';
    case 'cancelled': return 'warning';
    case 'no_show': return 'degraded';
    default: return 'inactive';
  }
}

export function statusLabel(t: Translate, status: string): string {
  return t(`bookings.status.${status}`, status);
}

/** A service's colour, or the brand colour when it has none. */
export function serviceColour(colour: string | null | undefined): string {
  return colour && /^#[0-9a-fA-F]{3,8}$/.test(colour) ? colour : 'var(--rr-color-primary)';
}

/**
 * Swatches offered for a service or a person. These are values the business
 * stores and every viewer sees the same, like a label colour, not theme
 * colours; the blocks drawn with them are tinted against the theme's surface.
 */
export const SWATCHES = [
  '#2563eb', '#0891b2', '#059669', '#65a30d', '#ca8a04', '#ea580c', '#dc2626', '#db2777', '#9333ea', '#4b5563'
] as const; // i18n-ignore: colour values

export const WEEKDAY_CODES: readonly bookings.Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
